import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeasonalDecoration from './SeasonalDecoration';

/**
 * Menú hamburguesa público — se usa en las 3 páginas públicas:
 *   /cotizador  → Cotizador de envíos
 *   /entregador → Portal del entregador (requiere NIP)
 *   /ofertas    → Ofertas activas (público)
 *
 * Se posiciona en la esquina superior izquierda (fixed) por encima del
 * contenido. Al pulsarlo abre un drawer lateral con overlay oscuro.
 * Respeta safe-area del notch en iOS.
 */
const TOOLS = [
  { to: '/cotizador', icon: '📐', label: 'Cotizador de envíos', desc: 'Calcula el costo por dirección' },
  { to: '/ofertas',   icon: '🏷️', label: 'Ofertas activas',      desc: 'Promociones vigentes' },
  { to: '/catalogo',  icon: '📦', label: 'Catálogo',              desc: 'Productos y precios' },
  { to: '/coreanos',  icon: '🥟', label: 'Productos coreanos',    desc: 'Botanas y bebidas por mayoreo' },
  { to: '/mayorista', icon: '🏬', label: 'Soy mayorista',         desc: 'Catálogo con tus precios' },
  { to: '/vendedor',  icon: '💼', label: 'Soy vendedor',          desc: 'Mi progreso y comisiones' },
  { to: '/entregador', icon: '🚚', label: 'Soy entregador',       desc: 'Entrar con NIP' },
  { to: '/ml-scanner', icon: '📮', label: 'Mercado Libre',        desc: 'Escáner de guías con NIP' },
];

export default function PublicToolsMenu({ current }) {
  const [open, setOpen] = useState(false);

  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <SeasonalDecoration />
      <button
        onClick={() => setOpen(true)}
        className="fixed z-[999] w-11 h-11 bg-white text-slate-800 shadow-lg rounded-full flex items-center justify-center border border-slate-200 hover:bg-slate-50 active:scale-95 transition"
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          left: 'calc(env(safe-area-inset-left, 0px) + 12px)',
        }}
        aria-label="Abrir menú"
        data-testid="public-menu-btn"
      >
        <span className="text-xl leading-none">☰</span>
      </button>

      {/* Drawer + overlay */}
      <div
        className={`fixed inset-0 z-[1000] transition ${open ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!open}
      >
        {/* Overlay */}
        <div
          onClick={() => setOpen(false)}
          className={`absolute inset-0 bg-black transition-opacity duration-200 ${open ? 'opacity-50' : 'opacity-0'}`}
        />
        {/* Panel */}
        <aside
          className={`absolute top-0 left-0 h-full w-[85%] max-w-[320px] bg-white shadow-2xl transform transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'}`}
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
          data-testid="public-menu-drawer"
        >
          <div className="px-5 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Herramientas</p>
              <h2 className="text-lg font-bold text-slate-800 mt-0.5">Menú</h2>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-9 h-9 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center text-xl"
              aria-label="Cerrar menú"
              data-testid="public-menu-close"
            >×</button>
          </div>

          <nav className="p-3 space-y-1">
            {TOOLS.map((t) => {
              const active = current === t.to;
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 p-3 rounded-xl transition ${active ? 'bg-red-50 border border-red-200' : 'hover:bg-slate-50 border border-transparent'}`}
                  data-testid={`public-menu-link-${t.to.replace('/','')}`}
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${active ? 'bg-red-100' : 'bg-slate-100'}`}>
                    {t.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold ${active ? 'text-red-700' : 'text-slate-800'}`}>{t.label}</p>
                    <p className="text-xs text-slate-500">{t.desc}</p>
                  </div>
                  {active && <span className="text-red-500 text-xs">●</span>}
                </Link>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 px-5 py-3 border-t border-slate-100 text-[11px] text-slate-400 text-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
            Herramientas públicas · Sistema POS
          </div>
        </aside>
      </div>
    </>
  );
}
